<template>
    <div class="file">
        <app-file-drop v-model="files" :label="label" :max="max" :accept="accept" :disabled="disabled" />
        <app-media-upload-preview :value="media.preview" :loading="uploading" @remove="removeMediaFile" />
    </div>
</template>

<script>
import { Media, MediaMarketing, MediaInvestor, MediaAdviser } from '@/models';
import request from '@/api/lib/request';
import { attachHeaders } from '@/api/lib/request';
import MediaMarketingApi from '@/api/MediaMarketingApi';
import MediaInvestorApi from '@/api/MediaInvestorApi';
import MediaAdviserApi from '@/api/MediaAdviserApi';

export default {
    name: 'MediaUpload',
    components: {
        AppMediaUploadPreview: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppMediaUploadPreview'
            )
    },
    props: {
        mediaClass: {
            type: Media,
            default: () => new Media()
        },
        value: {
            type: [Media, Array],
            default: null,
            validator: v => {
                if (v === null) {
                    return true;
                }

                if (v instanceof Media) {
                    return true;
                }

                if (Array.isArray(v) && v.every(m => m instanceof Media)) {
                    return true;
                }

                return false;
            }
        },
        method: {
            type: String,
            default: 'PUT'
        },
        label: {
            type: String,
            default: null
        },
        max: {
            type: Number,
            default: 1
        },
        accept: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        uploading: false
    }),
    computed: {
        media: {
            get() {
                if (this.value instanceof this.mediaClass) {
                    return this.value;
                }

                return new this.mediaClass();
            },
            set(v) {
                this.$emit('input', v);
            }
        },
        files: {
            get() {
                if (this.media.file_awaiting_upload) {
                    return [this.media.file_awaiting_upload];
                }
                return null;
            },
            set(files) {
                const media = this.media;

                if (files && files.length) {
                    media.file = null;
                    media.file_awaiting_upload = files[0];
                } else {
                    media.file_awaiting_upload = null;
                }

                this.media = media;
            }
        },
        apiClass() {
            if (this.mediaClass instanceof MediaMarketing) {
                return MediaMarketingApi;
            } else if (this.mediaClass instanceof MediaInvestor) {
                return MediaInvestorApi;
            } else if (this.mediaClass instanceof MediaAdviser) {
                return MediaAdviserApi;
            }

            return null;
        }
    },
    methods: {
        async upload(file = null) {
            this.uploading = true;

            try {
                file = file || this.media.file_awaiting_upload;

                if (!file) {
                    return;
                }

                if (!this.media.id) {
                    const response = await new this.apiClass().post(this.media);
                    this.media.id = response?.data?.created_id || response?.data?.id;
                }

                if (!this.media.send_file_url) {
                    throw new Error('Media ID not found when trying to create file PUT URL');
                }

                if (file) {
                    await request({
                        method: this.method,
                        url: this.media.send_file_url,
                        data: { file },
                        headers: attachHeaders({ 'Content-Type': 'multipart/form-data' })
                    });

                    const preview = this.media.preview;

                    this.media.file = {
                        filename: preview.name,
                        mimetype: preview.type,
                        size: preview.size
                    };

                    this.media.url = preview.src;

                    this.files = null;
                } else {
                    // PUT the media item to remove the file reference
                    await new this.apiClass().put(this.media.$toPayload());
                }
            } catch (error) {
                this.handleError(error, () => this.upload(file));
            }

            this.uploading = false;
        },
        removeMediaFile() {
            const media = this.media;
            media.url = null;
            media.file = null;
            media.file_awaiting_upload = null;
            this.media = media;
        }
    }
};
</script>

<style lang="scss" scoped>
.file {
    &__preview {
        overflow: hidden;
        background: var(--v-background-darken2);
        margin-top: 1rem;
        border-radius: 4px;
        img {
            display: block;
            margin: 0.8rem 1.2rem;
            height: 2.4rem;
            width: auto;
            border-radius: 4px;
        }
    }
}
</style>
